import { action, makeObservable } from "mobx";
import { createRef } from "react";
import { injectable } from "tsyringe";
import { ViewModel } from "../../lib/vvm";
import {
  normalizeTelegramWidgetAuthUser,
  type TelegramWidgetAuthUser,
} from "../../utils/telegramWebApp";

type TelegramWidgetSize = "large" | "medium" | "small";

export interface TelegramLoginWidgetViewProps {
  botUsername: string;
  onAuth: (payload: TelegramWidgetAuthUser) => void;
  size?: TelegramWidgetSize;
  cornerRadius?: number;
  requestAccess?: "write";
  lang?: string;
  disabled?: boolean;
}

const TELEGRAM_WIDGET_SRC = "https://telegram.org/js/telegram-widget.js?22";

@injectable()
export class TelegramLoginWidgetViewModel extends ViewModel<
  unknown,
  TelegramLoginWidgetViewProps
> {
  readonly containerRef = createRef<HTMLDivElement>();

  private callbackName = `__telegramWidgetAuth_${crypto.randomUUID().replace(/-/g, "")}`;

  constructor() {
    super();
    makeObservable(this);
  }

  protected onViewMounted(): void {
    this.renderWidget();
    this.reaction(
      () => [
        this.viewProps.botUsername,
        this.viewProps.size,
        this.viewProps.cornerRadius,
        this.viewProps.requestAccess,
        this.viewProps.lang,
      ],
      () => {
        this.renderWidget();
      },
    );
    this.addDisposer(() => {
      this.cleanupWidget();
    });
  }

  @action.bound
  private onWidgetAuth(rawPayload: unknown): void {
    if (this.viewProps.disabled) {
      return;
    }

    const payload = normalizeTelegramWidgetAuthUser(rawPayload);
    if (!payload) {
      return;
    }

    this.viewProps.onAuth(payload);
  }

  private renderWidget(): void {
    const container = this.containerRef.current;
    if (!container) {
      return;
    }

    const botUsername = this.viewProps.botUsername.trim();
    if (!botUsername) {
      this.cleanupContainer(container);
      return;
    }

    this.cleanupContainer(container);

    const globalWindow = window as unknown as Record<string, unknown>;
    globalWindow[this.callbackName] = (payload: unknown) =>
      this.onWidgetAuth(payload);

    const script = document.createElement("script");
    script.src = TELEGRAM_WIDGET_SRC;
    script.async = true;
    script.setAttribute("data-telegram-login", botUsername);
    script.setAttribute("data-size", this.viewProps.size || "large");
    script.setAttribute(
      "data-radius",
      String(Math.max(0, Number(this.viewProps.cornerRadius ?? 8))),
    );
    script.setAttribute("data-userpic", "false");
    script.setAttribute("data-lang", this.viewProps.lang || "ru");
    script.setAttribute("data-onauth", `${this.callbackName}(user)`);
    if (this.viewProps.requestAccess === "write") {
      script.setAttribute("data-request-access", "write");
    }

    container.appendChild(script);
  }

  private cleanupContainer(container: HTMLDivElement): void {
    while (container.firstChild) {
      container.removeChild(container.firstChild);
    }
  }

  private cleanupWidget(): void {
    const globalWindow = window as unknown as Record<string, unknown>;
    delete globalWindow[this.callbackName];
    if (this.containerRef.current) {
      this.cleanupContainer(this.containerRef.current);
    }
  }
}
